// Renders the input layer's device glyphs (`input/glyphs.ts`, pure data) as sticker key caps:
// key caps and a little mouse with the pressed button lit for KB+M, tinted face buttons and dark
// shoulder/trigger pills for gamepads, pink touch-button names for touch. Prompts re-render when
// the device, pad family or bindings change (`deviceAware`).
import { padButtonLabel, type Glyph, type GlyphAction, type InputDevice, type PadFamily } from '../input/glyphs';
import type { UiDeps } from './deps';
import { el } from './ui';

const INK = '#2A1A3A';

/** A small mouse drawing with the bound button filled (LMB / RMB / MMB). */
function mouseCap(label: string, title: string): HTMLElement {
  const side = label === 'LMB' ? 'l' : label === 'RMB' ? 'r' : label === 'MMB' ? 'm' : '';
  const node = el('span', { class: 'key mouse', attrs: { title, 'aria-label': title } });
  if (!side) {
    node.textContent = label;
    return node;
  }
  const fill = (s: string): string => (s === side ? '#FF4F9A' : '#FFFFFF');
  node.innerHTML = `<svg viewBox="0 0 16 22" class="mouse-svg" aria-hidden="true"><path d="M8 1.2C4.2 1.2 2 3.6 2 7.4v6.4c0 4 2.6 7 6 7s6-3 6-7V7.4C14 3.6 11.8 1.2 8 1.2z" fill="#FFFFFF" stroke="${INK}" stroke-width="1.6"/>`
    + `<path d="M2 8.6V7.4C2 3.6 4.2 1.2 7.4 1.2V8.6z" fill="${fill('l')}" stroke="${INK}" stroke-width="1.4" stroke-linejoin="round"/>`
    + `<path d="M14 8.6V7.4c0-3.8-2.2-6.2-5.4-6.2V8.6z" fill="${fill('r')}" stroke="${INK}" stroke-width="1.4" stroke-linejoin="round"/>`
    + `<rect x="6.9" y="3.4" width="2.2" height="3.6" rx="1.1" fill="${fill('m')}" stroke="${INK}" stroke-width="1"/></svg>`;
  return node;
}

/** A key cap element for one glyph. */
export function glyphCap(g: Glyph): HTMLElement {
  switch (g.kind) {
    case 'face': {
      const node = el('span', { class: 'key pad', text: g.label, attrs: { title: g.text } });
      if (g.color) node.style.setProperty('--pad', g.color);
      return node;
    }
    case 'shoulder':
      return el('span', { class: 'key shoulder', text: g.label, attrs: { title: g.text } });
    case 'trigger':
      return el('span', { class: 'key shoulder trigger', text: g.label, attrs: { title: g.text } });
    case 'stick':
      return el('span', { class: 'key pad stick', text: g.label, attrs: { title: g.text } });
    case 'dpad':
      return el('span', { class: 'key pad dpad', text: g.label, attrs: { title: g.text } });
    case 'system':
      return el('span', { class: 'key system', text: g.label, attrs: { title: g.text } });
    case 'touch':
      return el('span', { class: 'key touchy', text: g.label });
    case 'mouse':
      return mouseCap(g.label, g.text);
    case 'none':
      return el('span', { class: 'key none', text: '—' });
    default:
      return el('span', { class: `key${g.label.length > 3 ? ' wide' : ''}`, text: g.label });
  }
}

/** Face-button tints per pad family (the same palette as `input/glyphs.ts`). */
const FACE_TINTS: Readonly<Record<PadFamily, readonly string[]>> = {
  xbox: ['#3DDC97', '#FF5A4E', '#4CB8FF', '#FFC93C'],
  generic: ['#3DDC97', '#FF5A4E', '#4CB8FF', '#FFC93C'],
  playstation: ['#7EA6FF', '#FF6B7A', '#F59BD9', '#3DDCB4'],
  switch: [],
};

/** A cap for a Standard-mapping button index (rebinding tables). */
export function padButtonCap(btn: number, family: PadFamily): HTMLElement {
  const label = padButtonLabel(btn, family);
  const kind: Glyph['kind'] = btn <= 3 ? 'face' : btn <= 5 ? 'shoulder' : btn <= 7 ? 'trigger' : btn === 10 || btn === 11 ? 'stick' : btn >= 12 && btn <= 15 ? 'dpad' : 'system';
  const g: Glyph = { device: 'pad', kind, label, text: `(${label})` };
  const tint = kind === 'face' ? FACE_TINTS[family][btn] : undefined;
  if (tint) g.color = tint;
  return glyphCap(g);
}

/** The glyph cap for `action` on the current (or given) device with the live bindings. */
export function actionCap(deps: UiDeps, action: GlyphAction, device?: InputDevice): HTMLElement {
  return glyphCap(deps.services.input.glyph(action, device));
}

/** Plain text form, e.g. "[LMB]" or "(A)". */
export function actionText(deps: UiDeps, action: GlyphAction): string {
  return deps.services.input.glyph(action).text;
}

/** A hint chip: glyph + verb ("(A) Select"). */
export function hint(deps: UiDeps, action: GlyphAction, verb: string): HTMLElement {
  return el('span', { class: 'hint' }, actionCap(deps, action), el('span', { class: 'hint-verb', text: verb }));
}

/**
 * Rebuilds `host` with `build()` now and whenever the input device, pad family or bindings
 * change. Returns the unsubscribe function.
 */
export function deviceAware(deps: UiDeps, host: HTMLElement, build: () => (Node | null)[]): () => void {
  const render = (): void => host.replaceChildren(...build().filter((n): n is Node => n !== null));
  render();
  return deps.services.input.onChange(render);
}
